import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useAnimation } from '../context/AnimationContext';
import { ArrowRight, ExternalLink } from 'lucide-react';
import AnimationToggle from './AnimationToggle';
import { ALL_JOBS_URL } from '../config/careerLinks';
import './Header.css';

const Header = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [aboutDropdownOpen, setAboutDropdownOpen] = useState(false);
  const [cultureDropdownOpen, setCultureDropdownOpen] = useState(false);
  const [jobDropdownOpen, setJobDropdownOpen] = useState(false);
  const [workstyleDropdownOpen, setWorkstyleDropdownOpen] = useState(false);
  const [processDropdownOpen, setProcessDropdownOpen] = useState(false);
  const { animationEnabled, setAnimationEnabled } = useAnimation();
  const location = useLocation();
  const navigate = useNavigate();
  const { isAuthenticated, logout } = useAuth();

  // Scroll lock for mobile menu
  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = 'hidden';
      document.body.style.position = 'fixed';
      document.body.style.width = '100%';
    } else {
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.width = '';
    }
    
    return () => {
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.width = '';
    };
  }, [mobileMenuOpen]);

  // Keyboard navigation for mobile menu
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && mobileMenuOpen) {
        setMobileMenuOpen(false);
      }
    };

    if (mobileMenuOpen) {
      document.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [mobileMenuOpen]);

  const isActive = (path) => {
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  const isNewGraduateSite = location.pathname.startsWith('/newgraduate');

  // New Graduate specific links
  const newGraduateAboutLinks = [
    { title: '新卒向け：すぐ分かるFLARETECH', path: '/newgraduate/about/summary' }
  ];

  const newGraduateProcessLinks = [
    { title: '採用基準と選考プロセス', path: '/newgraduate/process/hiring-process' }
  ];

  const aboutLinks = [
    { title: '1ページで考えるFLARETECH', path: '/about/summary' },
    { title: 'FLARETECHの事業', path: '/about/business' },
    { title: '入社前に知ってほしい前提', path: '/about/prerequisites' },
    { title: 'AIと働くFLARETECHの現在地', path: '/about/ai' },
    { title: 'FLARETECHの物語', path: '/about/story' },
    { title: '私たちの進む方向', path: '/about/future' },
    { title: '企業の体験価値シフトの歴史', path: '/about/experience-shift' },
    { title: 'FLARETECHの経営陣', path: '/about/leadership' },
    { title: 'FLARETECHをもっと知るリンク集', path: '/about/more' }
  ];

  const cultureLinks = [
    { title: 'Purpose / Mission / Vision', path: '/culture/vision-mission' },
    { title: 'FLARETECHのバリュー（行動指針）', path: '/culture/values' },
    { title: 'FLARETECHのプリンシプル（行動原理）', path: '/culture/principles' },
    { title: 'FLARETECHの社内用語', path: '/culture/keywords' },
    { title: 'FLARETECHのナレッジ', path: '/culture/knowledge' }
  ];

  const jobLinks = [
    { title: '開発エンジニア', path: '/jobs/development-engineer' },
    { title: 'インフラエンジニア', path: '/jobs/infrastructure-engineer' },
    { title: 'DXエンジニア', path: '/jobs/dx-engineer' },
    { title: '営業', path: '/jobs/sales' }
  ];

  const workstyleLinks = [
    { title: '社員アンケート', path: '/workstyle/survey' },
    { title: 'Organization Design', path: '/workstyle/px' },
    { title: 'オンボーディング', path: '/workstyle/onboarding' },
    { title: '育成・学習機会', path: '/workstyle/development' },
    { title: '評価と報酬', path: '/workstyle/evaluation' },
    { title: 'キャリアパス', path: '/workstyle/career-path' },
    { title: '福利厚生', path: '/workstyle/benefits' },
    { title: '制度関連', path: '/workstyle/systems' }
  ];

  const processLinks = [
    { title: '選考プロセス', path: '/process/process' },
    { title: 'リエントリー制度', path: '/process/reentry' },
    { title: 'アルムナイ採用', path: '/process/alumni' },
    { title: 'よくある質問', path: '/process/faq' }
  ];

  return (
    <>
      <div className={`header-top-bar ${isNewGraduateSite ? 'red-bg' : ''}`}>
        <div className="header-top-bar-content">
          <div className="top-bar-nav">
            <Link to="/" className={`top-bar-btn ${!isNewGraduateSite ? 'active' : ''}`}>キャリア採用</Link>
            <Link to="/newgraduate" className={`top-bar-btn ${isNewGraduateSite ? 'active' : ''}`}>新卒採用</Link>
            <a href="https://flaretech.co.jp" target="_blank" rel="noopener noreferrer" className="top-bar-btn">コーポレートサイト</a>
          </div>
          <div className="top-bar-controls">
            <AnimationToggle 
              variant="power"
              enabled={animationEnabled}
              onChange={setAnimationEnabled}
              label="アニメーション"
              onRedBackground={isNewGraduateSite}
            />
            {isAuthenticated && (
              <button
                className="top-bar-btn logout-btn"
                onClick={() => {
                  logout();
                  navigate('/admin/login');
                }}
              >
                Logout
              </button>
            )}
          </div>
        </div>
      </div>
      
      <header className="header" role="banner">
        <div className="header-container">
          <Link to="/" className="header-logo">
            <img src="/common/logo-flaretech.svg" alt="FLARETECH" className="logo-image" />
            <span className="logo-careers">Careers</span>
          </Link>
          
          <nav className="header-nav" role="navigation" aria-label="メインナビゲーション">
            <div 
              className="nav-link-wrapper"
              onMouseEnter={() => setAboutDropdownOpen(true)}
              onMouseLeave={() => setAboutDropdownOpen(false)}
            >
              <Link to={isNewGraduateSite ? "/newgraduate/about" : "/about"} className={`nav-link ${isActive('/about') ? 'active' : ''}`}>
                About
              </Link>
              {aboutDropdownOpen && (
                <div className="dropdown-menu">
                  <div className="dropdown-menu-inner">
                    {isNewGraduateSite && newGraduateAboutLinks.map((link, index) => (
                      <Link 
                        key={`ng-${index}`}
                        to={link.path} 
                        className={`dropdown-item ${location.pathname === link.path ? 'active' : ''}`}
                        onClick={() => setAboutDropdownOpen(false)}
                      >
                        {link.title}
                      </Link>
                    ))}
                    {aboutLinks.map((link, index) => (
                      <Link 
                        key={index} 
                        to={link.path} 
                        className={`dropdown-item ${location.pathname === link.path ? 'active' : ''}`}
                        onClick={() => setAboutDropdownOpen(false)}
                      >
                        <span className="dropdown-item-text">{link.title}</span>
                        {isNewGraduateSite && (
                          <ExternalLink size={16} className="external-link-icon" />
                        )}
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>
            <div 
              className="nav-link-wrapper"
              onMouseEnter={() => setCultureDropdownOpen(true)}
              onMouseLeave={() => setCultureDropdownOpen(false)}
            >
              <Link to={isNewGraduateSite ? "/newgraduate/culture" : "/culture"} className={`nav-link ${isActive('/culture') ? 'active' : ''}`}>
                Culture
              </Link>
              {cultureDropdownOpen && (
                <div className="dropdown-menu">
                  <div className="dropdown-menu-inner">
                    {cultureLinks.map((link, index) => (
                      <Link 
                        key={index} 
                        to={link.path} 
                        className={`dropdown-item ${location.pathname === link.path ? 'active' : ''}`}
                        onClick={() => setCultureDropdownOpen(false)}
                      >
                        <span className="dropdown-item-text">{link.title}</span>
                        {isNewGraduateSite && (
                          <ExternalLink size={16} className="external-link-icon" />
                        )}
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>
            <div 
              className="nav-link-wrapper"
              onMouseEnter={() => setJobDropdownOpen(true)}
              onMouseLeave={() => setJobDropdownOpen(false)}
            >
              <Link to={isNewGraduateSite ? "/newgraduate/jobs" : "/jobs"} className={`nav-link ${isActive('/jobs') ? 'active' : ''}`}>
                Job
              </Link>
              {jobDropdownOpen && (
                <div className="dropdown-menu">
                  <div className="dropdown-menu-inner">
                    {jobLinks.map((link, index) => (
                      <Link 
                        key={index} 
                        to={link.path} 
                        className={`dropdown-item ${location.pathname === link.path ? 'active' : ''}`}
                        onClick={() => setJobDropdownOpen(false)}
                      >
                        <span className="dropdown-item-text">{link.title}</span>
                        {isNewGraduateSite && (
                          <ExternalLink size={16} className="external-link-icon" />
                        )}
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>
            <div className="nav-link-wrapper">
              <Link to="/people" className={`nav-link ${isActive('/people') ? 'active' : ''}`}>
                People
              </Link>
            </div>
            <div className="nav-link-wrapper">
              <Link to="/interview" className={`nav-link ${isActive('/interview') ? 'active' : ''}`}>
                Interview
                <span className="nav-new-badge">NEW</span>
              </Link>
            </div>
            <div 
              className="nav-link-wrapper"
              onMouseEnter={() => setWorkstyleDropdownOpen(true)}
              onMouseLeave={() => setWorkstyleDropdownOpen(false)}
            >
              <Link to={isNewGraduateSite ? "/newgraduate/workstyle" : "/workstyle"} className={`nav-link ${isActive('/workstyle') ? 'active' : ''}`}>
                Workstyle
              </Link>
              {workstyleDropdownOpen && (
                <div className="dropdown-menu">
                  <div className="dropdown-menu-inner">
                    {workstyleLinks.map((link, index) => (
                      <Link 
                        key={index} 
                        to={link.path} 
                        className={`dropdown-item ${location.pathname === link.path ? 'active' : ''}`}
                        onClick={() => setWorkstyleDropdownOpen(false)}
                      >
                        <span className="dropdown-item-text">{link.title}</span>
                        {isNewGraduateSite && (
                          <ExternalLink size={16} className="external-link-icon" />
                        )}
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>
            <div 
              className="nav-link-wrapper"
              onMouseEnter={() => setProcessDropdownOpen(true)}
              onMouseLeave={() => setProcessDropdownOpen(false)}
            >
              <Link to={isNewGraduateSite ? "/newgraduate/process" : "/process"} className={`nav-link ${isActive('/process') ? 'active' : ''}`}>
                Process
              </Link>
              {processDropdownOpen && (
                <div className="dropdown-menu">
                  <div className="dropdown-menu-inner">
                    {isNewGraduateSite && newGraduateProcessLinks.map((link, index) => (
                      <Link 
                        key={index} 
                        to={link.path} 
                        className={`dropdown-item ${location.pathname === link.path ? 'active' : ''}`}
                        onClick={() => setProcessDropdownOpen(false)}
                      >
                        <span className="dropdown-item-text">{link.title}</span>
                      </Link>
                    ))}
                    {processLinks.map((link, index) => (
                      <Link 
                        key={index} 
                        to={link.path} 
                        className={`dropdown-item ${location.pathname === link.path ? 'active' : ''}`}
                        onClick={() => setProcessDropdownOpen(false)}
                      >
                        <span className="dropdown-item-text">{link.title}</span>
                        {isNewGraduateSite && (
                          <ExternalLink size={16} className="external-link-icon" />
                        )}
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>
            {isNewGraduateSite && (
              <div className="nav-link-wrapper">
                <Link to="/newgraduate/internship" className="nav-link internship-btn">
                  <img 
                    src="/common/flame-icon-only.svg" 
                    alt="" 
                    style={{ height: '20px', width: 'auto' }}
                  />
                  Internship
                </Link>
              </div>
            )}
            <div className="nav-link-wrapper">
              <a href={ALL_JOBS_URL} target="_blank" rel="noopener noreferrer" className="nav-link entry-btn">
                <ArrowRight size={16} strokeWidth={2.5} />
                Entry
              </a>
            </div>
          </nav>

          <button 
            className={`mobile-menu-toggle ${mobileMenuOpen ? 'open' : ''}`}
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label={mobileMenuOpen ? 'メニューを閉じる' : 'メニューを開く'}
            aria-expanded={mobileMenuOpen}
            aria-controls="mobile-menu"
          >
            <span></span>
            <span></span>
            <span></span>
          </button>
        </div>
      </header>

      {/* Mobile menu backdrop */}
      {mobileMenuOpen && (
        <div 
          className="mobile-menu-backdrop" 
          onClick={() => setMobileMenuOpen(false)}
          aria-hidden="true"
        />
      )}

      <div 
        id="mobile-menu" 
        className={`mobile-menu ${mobileMenuOpen ? 'open' : ''}`}
        role="navigation"
        aria-label="モバイルメニュー"
      >
        <nav className="mobile-nav">
          <Link to={isNewGraduateSite ? "/newgraduate/about" : "/about"} className="mobile-nav-link" onClick={() => setMobileMenuOpen(false)}>
            About
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path d="M6 12l4-4-4-4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </Link>
          <Link to={isNewGraduateSite ? "/newgraduate/culture" : "/culture"} className="mobile-nav-link" onClick={() => setMobileMenuOpen(false)}>
            Culture
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path d="M6 12l4-4-4-4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </Link>
          <Link to={isNewGraduateSite ? "/newgraduate/jobs" : "/jobs"} className="mobile-nav-link" onClick={() => setMobileMenuOpen(false)}>
            Job
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path d="M6 12l4-4-4-4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </Link>
          <Link to="/people" className="mobile-nav-link" onClick={() => setMobileMenuOpen(false)}>
            People
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path d="M6 12l4-4-4-4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </Link>
          <Link to="/interview" className="mobile-nav-link" onClick={() => setMobileMenuOpen(false)}>
            Interview
            <span className="nav-new-badge">NEW</span>
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path d="M6 12l4-4-4-4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </Link>
          <Link to={isNewGraduateSite ? "/newgraduate/workstyle" : "/workstyle"} className="mobile-nav-link" onClick={() => setMobileMenuOpen(false)}>
            Workstyle
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path d="M6 12l4-4-4-4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </Link>
          <Link to={isNewGraduateSite ? "/newgraduate/process" : "/process"} className="mobile-nav-link" onClick={() => setMobileMenuOpen(false)}>
            Process
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path d="M6 12l4-4-4-4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </Link>
          {isNewGraduateSite && (
            <Link to="/newgraduate/internship" className="mobile-nav-link mobile-internship-btn" onClick={() => setMobileMenuOpen(false)}>
              <img 
                src="/common/flame-icon-only.svg" 
                alt="" 
                style={{ height: '20px', width: 'auto', marginRight: '8px' }}
              />
              Internship
            </Link>
          )}
          <a href={ALL_JOBS_URL} target="_blank" rel="noopener noreferrer" className="mobile-nav-link mobile-entry-btn" onClick={() => setMobileMenuOpen(false)}>
            Entry
          </a>
        </nav>
      </div>
    </>
  );
};

export default Header;
